import {AgentAction, ChatMessageRead} from "../schemas/chat_message";
import {ChatContext} from "../schemas/chat";
import {DocumentRequestCreate, JobRead, TagCreate} from "../schemas";
import {apiFetch, apiFetchRaw} from "./client";

export type CreateTurnParams = {
    chatId: string;
    content: string;
    senderId: string;
    sentFromChatInputState?: string;
    chatContexts?: ChatContext[];
    documentRequest?: DocumentRequestCreate;
    tagsToInfer?: TagCreate[];
};

export async function fetchMessagesByChat(
    chatId: string,
    limit?: number,
): Promise<ChatMessageRead[]> {
    return apiFetch<ChatMessageRead[]>("/chat_messages/by_chat", {
        params: {chat_id: chatId, ...(limit ? {limit} : {})},
    });
}

export async function fetchMessage(messageId: string): Promise<ChatMessageRead> {
    return apiFetch<ChatMessageRead>("/chat_messages/get", {
        params: {message_id: messageId},
    });
}

export async function fetchInFlightMessages(chatId: string): Promise<ChatMessageRead[]> {
    return apiFetch<ChatMessageRead[]>("/chat_messages/in_flight", {
        params: {chat_id: chatId},
    });
}

export async function streamChatTurn(
    params: CreateTurnParams,
    callbacks: {
        onAction?: (action: AgentAction) => void;
        onToken?: (text: string) => void;
        onJob?: (job: JobRead) => void;
    },
): Promise<{ message: ChatMessageRead; job?: JobRead }> {
    const resp = await apiFetchRaw("/chat_messages/stream_turn", {
        method: "POST",
        body: {
            chat_id: params.chatId,
            content: params.content,
            sender_id: params.senderId,
            sent_from_chat_input_state: params.sentFromChatInputState,
            chat_contexts: params.chatContexts,
            document_request: params.documentRequest,
            tags_to_infer: params.tagsToInfer,
        },
        headers: {Accept: "text/event-stream"},
    });

    const stream = resp.body;
    if (!stream) throw new Error("No response stream available");

    const reader = stream.pipeThrough(new TextDecoderStream()).getReader();

    let finalMessage: ChatMessageRead | null = null;
    let job: JobRead | undefined;
    let buffer = "";

    // eslint-disable-next-line no-constant-condition
    while (true) {
        const {value, done} = await reader.read();
        if (done) break;
        if (!value) continue;

        buffer += value;
        const frames = buffer.split("\n\n");
        // Keep the last (possibly incomplete) frame in the buffer
        buffer = frames.pop() ?? "";

        for (const frame of frames) {
            if (!frame.trim()) continue;

            const lines = frame.split("\n");
            const eventLine = lines.find((l) => l.startsWith("event:"));
            const dataLines = lines.filter((l) => l.startsWith("data:"));

            if (!eventLine || dataLines.length === 0) continue;

            const event = eventLine.replace("event:", "").trim();
            const dataPayload = dataLines.map((l) => l.replace("data:", "").trimStart()).join("");

            switch (event) {
                case "action":
                    if (callbacks.onAction) {
                        try {
                            callbacks.onAction(JSON.parse(dataPayload) as AgentAction);
                        } catch { /* ignore parse errors */ }
                    }
                    break;
                case "token":
                    callbacks.onToken?.(dataPayload);
                    break;
                case "message":
                    try {
                        finalMessage = JSON.parse(dataPayload) as ChatMessageRead;
                    } catch (e) {
                        console.error("Failed to parse message event:", e);
                    }
                    break;
                case "job":
                    try {
                        job = JSON.parse(dataPayload) as JobRead;
                        callbacks.onJob?.(job);
                    } catch { /* ignore */ }
                    break;
                case "error":
                    try {
                        const err = JSON.parse(dataPayload);
                        throw new Error(err.error || "Stream error");
                    } catch (e) {
                        if (e instanceof Error && e.message !== "Stream error") throw e;
                        throw new Error("Unknown stream error");
                    }
            }
        }
    }

    if (!finalMessage) {
        throw new Error("Stream ended without a final message event");
    }

    return {message: finalMessage, job};
}
